<template>
  <v-container
    id="accounts-view"
    tag="section"
  >
    <v-card>
      <v-card-title>
        <v-row>
          <v-col cols="12" sm="8" md="6">
            {{ $t('account.accounts') }}
          </v-col>
          <v-col cols="12" sm="4" md="6" class="text-sm-right">
            <v-btn color="primary" @click="createItemDialog">
              {{ $t('account.create-account') }}
            </v-btn>
            <v-btn color="secondary" :aria-label="$t('account.refresh-accounts')" @click="refreshItems(true)">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col>
            <v-text-field
              v-model="search"
              class="searchBox"
              append-icon="mdi-magnify"
              :aria-label="$t('global.text-field')+' '+$t('global.search') +' '+ $t('account.accounts')"
              single-line
              hide-details
            />
          </v-col>
          <v-spacer />
          <v-spacer />
          <v-spacer />
          <v-col>
            <v-switch
              v-model="filterOutExpiredAccounts"
              :label="$t('accounts.hide-expired')"
            />
          </v-col>
        </v-row>

        <v-data-table
          v-model="selected"
          filterable
          :headers="headersForUser"
          :items="items"
          item-key="id"
          :loading="loading"
          :search="search"
          :items-per-page="30"
          :footer-props="{'items-per-page-options':[15, 30, 50, 100, -1]}"
        >
          <template v-slot:item.displayName="{ item }">
            <a :href="createAccountDashboardURL(item)">
              {{ item.displayName }}
            </a>
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip :class="[getDisplayStatus(item)]" class="status">
              {{ $t(getDisplayStatusText(item)) }}
            </v-chip>
          </template>
          <template v-slot:item.created="{ item }">
            {{ displayTime(item.created) }}
          </template>
          <template v-slot:item.expires="{ item }">
            {{ displayTime(item.expires) }}
          </template>
          <template v-slot:item.accountType="{ item }">
            {{ $t('accountType.' + item.accountType) }}
          </template>
          <template v-slot:item.managingAccountID="{ item }">
            {{ ownerAccountName(item.managingAccountID) }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-tooltip v-if="item.status !== 'deleted'" bottom>
              <template #activator="{ on }">
                <v-icon
                  class="mr-2"
                  :aria-label="$t('account.edit.edit-account', [item.displayName])"
                  v-on="on"
                  @click="editItem(item)"
                >
                  mdi-pencil-outline
                </v-icon>
              </template>
              <span>{{ $t('edit') }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template #activator="{ on }">
                <v-icon
                :aria-label="item.status !== 'deleted'? $t('account.edit.delete-account', [item.displayName]) : $t('account.edit.revert-delete-account', [item.displayName])"
                 v-on="on" @click="deleteItem(item)" 
                >
                  {{ item.status === 'deleted' ? 'mdi-delete-off-outline' : 'mdi-delete-outline' }}
                </v-icon>
              </template>
              <template>
                <span v-if="item.status !== 'deleted'">{{ $t('delete') }}</span>
                <span v-else>{{ $t('revert-delete') }}</span>
              </template>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-row justify="center">
      <v-dialog
        v-model="editDialogVisible"
        persistent
        max-width="600px"
      >
        <account-view
          :id="editedItemID"
          ref="accountEditDialog"
          :model="editedItem"
          @cancel="closeEditDialog"
          @save="saveEditDialog"
          @error="errorDialog"
        />
      </v-dialog>
      <v-dialog v-model="deleteDialogVisible" max-width="500px">
        <v-card>
          <v-card-title>{{ $t('global.confirm') }}</v-card-title>
          <v-card-text>
            <span v-if="editedItem && editedItem.status !== 'deleted'">{{ $t('account.confirm-delete') }} </span>
            <span v-if="editedItem && editedItem.status === 'deleted'">{{ $t('account.confirm-revert-delete') }} </span>
            <span class="font-weight-bold">{{ selectedAccountName }}</span>?
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn color="blue" @click="closeDeleteDialog">
              {{ $t('global.no') }}
            </v-btn>
            <v-btn color="red" @click="confirmDeleteItem">
              {{ $t('global.yes') }}
            </v-btn>
            <v-spacer />
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-row>

    <v-dialog v-model="error" max-width="500px">
      <v-card>
        <v-card-title>{{ $t('global.error') }}</v-card-title>
        <v-card-text>{{ errorMsg }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="blue" @click="error = null">
            {{ $t('global.close') }}
          </v-btn>
          <v-spacer />
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-overlay :value="busy">
      <v-progress-circular
        indeterminate
        size="64"
      />
    </v-overlay>
  </v-container>
</template>

<script>
  // import api from '@/helpers/api.js'
  import { Roles } from '@/helpers/users.js'
  import { Accounts, accountStatus } from '@/helpers/accounts.js'
  import accountView from './AccountsEdit.vue'
  import moment from 'moment'

  export default {
    name: 'Main',

    components: {
      'account-view': accountView,
    },


    data () {
      return {
        loading: false,
        busy: false,
        search: '',
        error: false,
        errorMsg: '',
        selected: [],
        editDialogVisible: false,
        deleteDialogVisible: false,
        items: [],
        accounts: [],
        filterOutExpiredAccounts: true,
        headers: [
          { text: 'account.label.accountName', sortable: true, value: 'displayName' },
          { text: 'account.label.created', sortable: true, value: 'created' },
          { text: 'account.label.expires', sortable: true, value: 'expires' },
          { text: 'account.label.status', sortable: true, value: 'status' },
          { text: 'account.label.type', sortable: true, value: 'accountType' },
          { text: 'account.label.tag', sortable: true, value: 'tag' },
          { text: 'account.label.actions', sortable: false, value: 'actions' },
        ],
        sysadminOnlyHeaders: [
          { text: 'account.label.accountName', sortable: true, value: 'displayName' },
          { text: 'account.label.created', sortable: true, value: 'created' },
          { text: 'account.label.expires', sortable: true, value: 'expires' },
          { text: 'account.label.status', sortable: true, value: 'status' },
          { text: 'account.label.type', sortable: true, value: 'accountType' },
          { text: 'account.label.tag', sortable: true, value: 'tag' },
          { text: 'account.label.owner', sortable: true, value: 'managingAccountID' },
          { text: 'account.label.actions', sortable: false, value: 'actions' },
        ],
        editedItem: null,
        editedIndex: -1,
        editedItemID: undefined,
        model: {},
        username: 'tom',
        statusTexts: {
          active: 'account.status.active',
          awaitingActivation: 'account.status.ready',
          expiring: 'account.status.expiring-soon',
          expired: 'account.status.expired',
          suspended: 'account.status.deactivated',
          deleted: 'account.status.deleted',
        },
        rowsPerPage: 15,
      }
    },

    computed: {
      selectedAccountName: function () {
        return (this.editedItem) ? this.editedItem.displayName : ''
      },
      headersForUser: function () {
        if (this.$store.getters.userHasRole(Roles.SYSADMIN)) {
          return this.sysadminOnlyHeaders.map((x) => { return { ...x, text: this.$t(x.text) } })
        }
        return this.headers.map((x) => { return { ...x, text: this.$t(x.text) } })
      },
      accountStatus () {
        return [accountStatus.ACTIVE, accountStatus.AWAITINGACTIVATION, accountStatus.SUSPENDED].map(x => { return { text: x, value: x } })
      },
    },
    watch: {
      async filterOutExpiredAccounts (newState, oldState) {
        Accounts.SetDirty()
        this.loading = true
        this.items = []
        this.accounts = await Accounts.GetAll(!newState)
        this.loading = false
        this.items = this.accounts.accounts
      },
    },
    async created () {
      this.editedItemID = null
      this.refreshItems(false)
    },
    methods: {
      isSuperUser () {
        return this.$store.getters.userHasRole(Roles.SUPER)
      },
      errorDialog (error) {
        this.error = true
        this.errorMsg = error
      },
      createAccountDashboardURL (item) {
        return `#/admin/accounts/dashboard?account=${item.id}`
      },
      ownerAccountName (id) {
        if (!this.items) {
          return id
        }
        const account = this.accounts.accounts.find(x => x.id === id)
        if (account) return account.displayName

        return (id)
      },
      getDisplayStatus (item) {
        return item.status
      },
      getDisplayStatusText (item) {
        const displayStatus = this.getDisplayStatus(item)
        return this.statusTexts[displayStatus]
      },
      displayTime (time) {
        return this.$i18n.d(moment(time), 'short')
      },
      async createItemDialog () {
        // console.log('createItemDialog')
        this.editedIndex = -1
        // this.editedItem = Object.assign({}, this.defaultItem)
        if (this.$refs.accountEditDialog) {
          await this.$refs.accountEditDialog.populate()
        }
        this.editedItemID = undefined
        this.editDialogVisible = true
      },
      async editItem (item) {
        this.editedIndex = this.items.indexOf(item)
        this.editDialogVisible = true
        if (this.$refs.accountEditDialog) {
          await this.$refs.accountEditDialog.populate(item.id)
        }
        this.editedItemID = item.id
      },
      deleteItem (item) {
        console.log(item)
        this.editedItem = item
        this.editedIndex = this.items.indexOf(item)
        this.deleteDialogVisible = true
      },
      closeEditDialog () {
        this.editDialogVisible = false
        // this.$refs.accountEditDialog.reset()
      },
      async saveEditDialog (response) {
        // console.log(this.editedIndex + ' save data ' + JSON.stringify(response))
        if (this.editedIndex === -1)
        {
          // new account add
          await this.refreshItems(false)
        }
        else
        {
          Object.assign(this.items[this.editedIndex], response)
          // console.log('saveEditDialog ' + JSON.stringify(response))
        }
        this.editDialogVisible = false
        // this.$refs.accountEditDialog.reset()
      },
      async confirmDeleteItem () {
        this.busy = true
        this.closeDeleteDialog()
        // this.items.splice(this.editedIndex, 1)
        try {
          if (this.editedItem.status !== accountStatus.DELETED) await Accounts.SoftDelete(this.editedItem.id)
          else await Accounts.RevertSoftDelete(this.editedItem.id)
          await this.refreshItems(true)
        } catch (ex) {
          this.errorDialog(ex.response.data)
        }
        this.busy = false
      },
      filterExpiredAccounts () {
        return this.accounts && this.accounts.accounts.filter(account => account.status !== accountStatus.EXPIRED)
      },
      async refreshItems (forceReload) {
        this.loading = true
        if (forceReload) {
          Accounts.SetDirty()
        }
        try {
          this.accounts = await Accounts.GetAll(!this.filterOutExpiredAccounts) // api.getAccounts()
        } catch (ex) {
          this.errorDialog(ex.response.data)
        }
        this.items = this.accounts.accounts // this.filterExpiredAccounts()
        // console.log({ ite: this.items })
        this.loading = false
      },
      closeDeleteDialog () {
        this.deleteDialogVisible = false
        this.$nextTick(() => {
          this.editedItem = Object.assign({}, this.defaultItem)
          this.editedIndex = -1
        })
      },

    },
  }
</script>

<style scoped>
.suspended { background-color: #979797 !important; color: white; }
.expiring  { background-color: #ffa40c !important; color: black; }
.awaitingActivation { background-color: #3dbfb7 !important; color: black; }
.active { background-color: #4bbd4b !important; color: black; }
.expired { background-color: #ff3f3f !important; color: black; }
.deleted { background-color: #6d6d6d !important; color: white; }
</style>

<!--
<template>
  <v-container>
    hi
  </v-container>
</template>

<script>
  export default {
    name: 'accounts',
  }
</script>

<style>

</style>
-->
